import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GradientText from "./GradientText";
import Seo from "./Seo";
import BackgroundVideo from "./BackgroundVideo";

const neonColors = ["#fb923c", "#f97316", "#ea580c", "#f59e0b", "#fb923c"];

export interface MenuItem {
  name: string;
  description: string;
  price?: number | string;
  sizes?: MenuItemSizes;
  image?: string;
  tags: string[];
}

export interface MenuItemSize {
  name: string;
  price: number | string;
}

export type MenuItemSizes = MenuItemSize[] | Record<string, number | string>;

export interface MenuCategoryModifier {
  name: string;
  price: number | string;
}

export interface MenuCategoryModifiers {
  title: string;
  items: MenuCategoryModifier[];
}

export interface MenuCategory {
  name: string;
  id: string;
  description?: string;
  headerBackgroundImage?: string;
  headerImage?: string;
  modifiers?: MenuCategoryModifiers;
  items: MenuItem[];
}

interface MenuData {
  categories: MenuCategory[];
}

interface MenuCatalogAction {
  label: string;
  to: string;
  variant?: "primary" | "secondary";
}

interface MenuCatalogProps {
  seoTitle: string;
  seoDescription: string;
  seoPath: string;
  seoImage: string;
  pageTitle: string;
  pageSubtitle: string;
  dataUrl: string;
  actions?: MenuCatalogAction[];
  actionsIntro?: string;
}

const tagLabels: Record<string, string> = {
  popular: "Popular",
  signature: "Signature",
  vegetarian: "Vegetarian",
  spicy: "Spicy",
  new: "New",
};

export default function MenuCatalog({
  seoTitle,
  seoDescription,
  seoPath,
  seoImage,
  pageTitle,
  pageSubtitle,
  dataUrl,
  actions = [],
  actionsIntro,
}: MenuCatalogProps) {
  const [menuData, setMenuData] = useState<MenuData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function loadMenu() {
      try {
        const response = await fetch(dataUrl, { cache: "no-store" });
        if (!response.ok) throw new Error("Failed to load menu");
        const data = await response.json();
        setMenuData(data);
        setLoading(false);
      } catch (err) {
        console.error("Error loading menu:", err);
        setError(true);
        setLoading(false);
      }
    }

    loadMenu();
  }, [dataUrl]);

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={seoPath}
        image={seoImage}
      />
      <section className="relative pt-[calc(80px+60px)] pb-[60px] px-6 text-center overflow-hidden">
        <BackgroundVideo wrapperClassName="absolute inset-0 -z-10" />
        <div className="container">
          <GradientText
            colors={neonColors}
            animationSpeed={6}
            className="text-[clamp(2.5rem,8vw,4.5rem)] mb-4 font-display font-semibold"
          >
            {pageTitle}
          </GradientText>
          <p className="text-xl text-text-secondary font-light max-w-[720px] mx-auto">
            {pageSubtitle}
          </p>
          {actions.length > 0 && (
            <div className="mt-8 flex flex-col items-center gap-4">
              {actionsIntro && (
                <p className="text-white/80 text-sm md:text-base font-medium tracking-[0.02em]">
                  {actionsIntro}
                </p>
              )}
              <div className="flex flex-wrap justify-center gap-4">
                {actions.map((action) => (
                  <Link
                    key={`${action.to}-${action.label}`}
                    to={action.to}
                    className={
                      action.variant === "secondary"
                        ? "btn btn-large bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-white hover:text-bg-primary"
                        : "btn btn-primary btn-large"
                    }
                  >
                    {action.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-[60px] pb-[100px] bg-bg-secondary min-h-[60vh]">
        <div className="container">
          {loading && (
            <div className="text-center py-20 text-text-secondary">
              <div className="loading-spinner"></div>
              <p>Loading delicious items...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <span className="text-[4rem] block mb-5">😅</span>
              <h3 className="font-body font-semibold text-text-primary mb-2">
                Oops! Couldn't load the menu
              </h3>
              <p className="text-text-secondary">Please try refreshing the page</p>
            </div>
          )}

          {!loading && !error && (
            <div className="flex flex-col gap-12" id="menu-sections">
              {menuData?.categories.map((category) => (
                <section key={`${category.id}-${category.name}`} className="space-y-5">
                  <SectionHeader category={category} />
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {category.items.map((item) => (
                      <MenuItemCard
                        key={`${category.id}-${category.name}-${item.name}`}
                        item={item}
                      />
                    ))}
                  </div>
                  <CategoryModifiers modifiers={category.modifiers} />
                </section>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function SectionHeader({ category }: { category: MenuCategory }) {
  const backgroundImage = category.headerBackgroundImage ?? category.headerImage ?? "";
  const hasImage = backgroundImage.trim() !== "";

  return (
    <div className="relative min-h-[180px] md:min-h-[220px] rounded-2xl overflow-hidden border border-border-default bg-bg-card">
      {hasImage && (
        <img
          src={backgroundImage}
          alt={category.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div
        className={`absolute inset-0 ${
          hasImage ? "bg-gradient-to-r from-black/80 via-black/55 to-black/30" : ""
        }`}
      />
      <div className="relative z-10 h-full p-6 md:p-8 flex flex-col justify-center">
        <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] font-display font-semibold text-text-primary mb-3">
          {category.name}
        </h2>
        {category.description && (
          <p className="text-text-secondary max-w-[900px] leading-relaxed">
            {category.description}
          </p>
        )}
      </div>
    </div>
  );
}

function MenuItemCard({ item }: { item: MenuItem }) {
  const hasImage = item.image && item.image.trim() !== "";
  const tags = item.tags || [];
  const sizes = normalizeSizes(item.sizes);

  const formatPrice = (value: number | string) =>
    typeof value === "number" ? `$${value.toFixed(2)}` : value;

  const displayPrice = item.price !== undefined ? formatPrice(item.price) : "";

  return (
    <div className="bg-bg-card border border-border-default rounded-xl transition-all duration-300 hover:border-accent-purple hover:shadow-md-custom hover:shadow-glow-pink animate-slide-up">
      {hasImage && (
        <div className="w-full aspect-square overflow-hidden rounded-t-xl relative bg-bg-elevated">
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-400 hover:scale-105"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.parentElement!.innerHTML =
                '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-bg-elevated to-bg-tertiary"><span class="text-[2.5rem] opacity-50">🍽️</span></div>';
            }}
          />
        </div>
      )}
      <div className="p-5 flex flex-col h-full">
        <div className="flex justify-between items-start gap-4 mb-2">
          <h3 className="font-body font-semibold text-lg text-text-primary leading-tight">
            {item.name}
          </h3>
          {displayPrice && (
            <span className="font-bold text-base text-accent-pink whitespace-nowrap">
              {displayPrice}
            </span>
          )}
        </div>
        {sizes.length > 0 && (
          <div className="mb-3 border border-border-default/70 rounded-lg px-3 py-2 space-y-1.5 bg-bg-elevated/30">
            {sizes.map((size) => (
              <div key={`${item.name}-${size.name}`} className="flex justify-between gap-3 text-sm">
                <span className="text-text-secondary">{size.name}</span>
                <span className="font-semibold text-accent-pink whitespace-nowrap">
                  {formatPrice(size.price)}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="text-text-secondary text-[0.9rem] leading-relaxed mb-[10px] flex-1">
          {item.description}
        </p>
        {tags.length > 0 && (
          <div className="flex gap-[6px] flex-wrap">
            {tags.map((tag) => (
              <span key={tag} className={`tag tag-${tag}`}>
                {tagLabels[tag] || tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryModifiers({
  modifiers,
}: {
  modifiers?: MenuCategoryModifiers;
}) {
  if (!modifiers || !modifiers.items.length) return null;

  return (
    <details className="group rounded-xl border border-border-default bg-bg-card overflow-hidden">
      <summary className="list-none cursor-pointer px-5 py-4 flex items-center justify-between gap-4">
        <span className="font-body font-semibold text-text-primary">
          {modifiers.title}
        </span>
        <span className="text-text-secondary text-sm transition-transform duration-300 group-open:rotate-180">
          ^
        </span>
      </summary>
      <div className="border-t border-border-default px-5 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
          {modifiers.items.map((modifier) => (
            <div
              key={`${modifier.name}-${modifier.price}`}
              className="flex items-center justify-between gap-4 text-sm"
            >
              <span className="text-text-secondary">{modifier.name}</span>
              <span className="font-semibold text-accent-pink whitespace-nowrap">
                {formatModifierPrice(modifier.price)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </details>
  );
}

function normalizeSizes(sizes?: MenuItemSizes): MenuItemSize[] {
  if (!sizes) return [];

  if (Array.isArray(sizes)) {
    return sizes.filter(
      (size): size is MenuItemSize =>
        typeof size?.name === "string" &&
        (typeof size?.price === "number" || typeof size?.price === "string"),
    );
  }

  return Object.entries(sizes).map(([name, price]) => ({ name, price }));
}

function formatModifierPrice(value: number | string) {
  if (typeof value === "number") {
    if (value === 0) return "Free";
    return `$${value.toFixed(2)}`;
  }

  return value;
}
